import React, { useCallback, useRef, useState } from "react";

type SelectionRect = {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
};

export type NormalizedRect = {
  x: number;
  y: number;
  width: number;
  height: number;
  pageNumber: number;
};

type SnipOverlayProps = {
  pageNumber: number;
  isActive: boolean;
  onSelectionComplete: (rect: NormalizedRect) => void;
};

export const SnipOverlay: React.FC<SnipOverlayProps> = ({
  pageNumber,
  isActive,
  onSelectionComplete,
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [selection, setSelection] = useState<SelectionRect | null>(null);

  const getRelativePosition = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const rect = overlayRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isActive) return;
      e.preventDefault();
      e.stopPropagation();
      const pos = getRelativePosition(e);
      setIsDragging(true);
      setSelection({
        startX: pos.x,
        startY: pos.y,
        endX: pos.x,
        endY: pos.y,
      });
    },
    [isActive, getRelativePosition]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isDragging || !selection) return;
      e.preventDefault();
      const pos = getRelativePosition(e);
      setSelection((prev) => (prev ? { ...prev, endX: pos.x, endY: pos.y } : null));
    },
    [isDragging, selection, getRelativePosition]
  );

  const handleMouseUp = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isDragging || !selection || !overlayRef.current) return;
      e.preventDefault();
      setIsDragging(false);

      const rect = overlayRef.current.getBoundingClientRect();
      const x = Math.min(selection.startX, selection.endX);
      const y = Math.min(selection.startY, selection.endY);
      const width = Math.abs(selection.endX - selection.startX);
      const height = Math.abs(selection.endY - selection.startY);

      // Minimum selection size check (at least 5px in each direction)
      if (width < 5 || height < 5) {
        setSelection(null);
        return;
      }

      // Normalize to 0.0-1.0 relative to the page element
      const normalized: NormalizedRect = {
        x: Math.max(0, Math.min(1, x / rect.width)),
        y: Math.max(0, Math.min(1, y / rect.height)),
        width: Math.min(1, width / rect.width),
        height: Math.min(1, height / rect.height),
        pageNumber,
      };

      onSelectionComplete(normalized);
      setSelection(null);
    },
    [isDragging, selection, pageNumber, onSelectionComplete]
  );

  // Calculate display rectangle
  const displayRect = selection
    ? {
        left: Math.min(selection.startX, selection.endX),
        top: Math.min(selection.startY, selection.endY),
        width: Math.abs(selection.endX - selection.startX),
        height: Math.abs(selection.endY - selection.startY),
      }
    : null;

  if (!isActive) return null;

  return (
    <div
      ref={overlayRef}
      className="absolute inset-0 z-10"
      style={{ cursor: "crosshair" }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      {displayRect && displayRect.width > 0 && displayRect.height > 0 && (
        <div
          className="pointer-events-none absolute border-2 border-primary bg-primary/10"
          style={{
            left: displayRect.left,
            top: displayRect.top,
            width: displayRect.width,
            height: displayRect.height,
          }}
        />
      )}
    </div>
  );
};
